import { useEffect, useRef, useState, type ComponentType } from 'react'
import { Link } from '@tanstack/react-router'
import {
  ChevronDown,
  Sparkles,
  HeartPulse,
  PauseCircle,
  AlertTriangle,
  ShieldHalf,
  LifeBuoy,
  Search,
  FileText,
  Library,
  KeyRound,
  HeartHandshake,
  Lock,
} from 'lucide-react'
import { useStatePack } from '@/lib/state-context'
import StatePicker from './StatePicker'

type IconCmp = ComponentType<{ className?: string }>

const BASICS: { slug: string; label: string; icon: IconCmp }[] = [
  { slug: 'new-age-of-scams', label: 'The New Age of Scams', icon: Sparkles },
  { slug: 'emotional-hijack', label: 'The Emotional Hijack', icon: HeartPulse },
  { slug: 'pause-and-verify', label: 'Pause and Verify', icon: PauseCircle },
  { slug: 'warning-signs', label: 'Spot the Warning Signs', icon: AlertTriangle },
  { slug: 'digital-self-defense', label: 'Digital Self-Defense', icon: ShieldHalf },
  { slug: 'if-youre-scammed', label: 'If You’re Scammed', icon: LifeBuoy },
]

const TOOLS: { slug: string; label: string; icon: IconCmp; locked?: boolean }[] = [
  { slug: 'tools/scam-checker', label: 'Scam Checker', icon: Search, locked: true },
  { slug: 'articles', label: 'Scam Prevention Articles', icon: FileText },
  { slug: 'scam-library', label: 'Scam Library', icon: Library },
  { slug: 'tools/password-check', label: 'Password Check', icon: KeyRound, locked: true },
  { slug: 'tools/charity-checker', label: 'Charity Checker', icon: HeartHandshake, locked: true },
]

export default function SiteHeader() {
  const pack = useStatePack()
  const [openMenu, setOpenMenu] = useState<'basics' | 'tools' | null>(null)
  const headerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!openMenu) return
    const handleClick = (e: MouseEvent) => {
      if (!headerRef.current?.contains(e.target as Node)) setOpenMenu(null)
    }
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenMenu(null)
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [openMenu])

  // Logo always returns to the national homepage. State pages are reachable
  // via /states/$slug, but the brand mark is the global home anchor.
  const homePath = '/'

  return (
    <div ref={headerRef} className="text-white">
      <div className="bg-[#0a0620]">
        <header className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-6 py-4">
          <Link to={homePath} className="flex shrink-0 items-center text-white">
            <span className="text-2xl font-semibold tracking-tight">
              ScamDefense
            </span>
          </Link>

          <nav className="order-3 flex w-full items-center justify-center gap-1 sm:gap-6 lg:order-none lg:w-auto">
            <NavMenu
              label="Scam Defense Basics"
              isOpen={openMenu === 'basics'}
              onToggle={() => setOpenMenu(openMenu === 'basics' ? null : 'basics')}
              items={BASICS.map(b => ({
                to: `/basics/${b.slug}`,
                label: b.label,
                icon: b.icon,
              }))}
              onItemClick={() => setOpenMenu(null)}
              widthClass="w-[17rem]"
            />
            <NavMenu
              label="Tools & Resources"
              isOpen={openMenu === 'tools'}
              onToggle={() => setOpenMenu(openMenu === 'tools' ? null : 'tools')}
              items={TOOLS.map(t => ({
                to: `/${t.slug}`,
                label: t.label,
                icon: t.icon,
                locked: t.locked,
              }))}
              onItemClick={() => setOpenMenu(null)}
            />
            <Link
              to="/about"
              className="cursor-pointer rounded-md px-3 py-2 text-base font-bold tracking-tight text-white/85 hover:text-white"
              activeProps={{ className: 'cursor-pointer rounded-md px-3 py-2 text-base font-bold tracking-tight text-white' }}
            >
              About
            </Link>
          </nav>

          <div className="flex shrink-0 items-center gap-2">
            <StatePicker />
          </div>
        </header>
      </div>
    </div>
  )
}

function NavMenu({
  label,
  isOpen,
  onToggle,
  items,
  onItemClick,
  widthClass = 'w-[22rem]',
}: {
  label: string
  isOpen: boolean
  onToggle: () => void
  items: { to: string; label: string; icon: IconCmp; locked?: boolean }[]
  onItemClick: () => void
  widthClass?: string
}) {
  return (
    <div className="relative">
      <button
        onClick={onToggle}
        className="flex cursor-pointer items-center gap-1.5 rounded-md px-3 py-2 text-base font-bold tracking-tight text-white/85 hover:text-white"
      >
        {label}
        <ChevronDown
          className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {isOpen && (
        <div className={`absolute left-1/2 z-30 mt-3 -translate-x-1/2 rounded-2xl bg-white p-2 text-slate-700 shadow-[0_20px_50px_-12px_rgba(15,15,40,0.35)] ring-1 ring-slate-200/70 ${widthClass}`}>
          <ul>
            {items.map(item => {
              const Icon = item.icon
              if (item.locked) {
                return (
                  <li key={item.to}>
                    <div
                      aria-disabled="true"
                      title="Coming soon"
                      className="flex cursor-not-allowed items-center gap-3.5 rounded-xl px-3 py-1.5 text-[15px] font-medium text-slate-400"
                    >
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center text-slate-300">
                        <Icon className="h-5 w-5" />
                      </span>
                      <span className="flex-1 leading-tight">{item.label}</span>
                      <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-500">
                        <Lock className="h-2.5 w-2.5" />
                        Soon
                      </span>
                    </div>
                  </li>
                )
              }
              return (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    onClick={onItemClick}
                    className="group flex items-center gap-3.5 rounded-xl px-3 py-1.5 text-[15px] font-medium text-slate-700 hover:bg-violet-50/60 hover:text-slate-900"
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center text-violet-600 transition-colors group-hover:text-violet-700">
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="flex-1 leading-tight">{item.label}</span>
                  </Link>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}
